// frontend/src/components/CampaignManager/dialogs/CampaignDetailDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme,
  FormControlLabel, Switch, Alert, Box, Typography, Tabs, Tab, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Chip, Tooltip
} from '@mui/material';
import { PencilSimple, Trash, Plus } from '@phosphor-icons/react';
import { Campaign, Lottery, Versand, Werbeweg } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface Props {
  open: boolean;
  campaign: Campaign | null;
  lotteries: Lottery[];
  werbewege: Werbeweg[];
  versandList: Versand[];
  onClose: () => void;
  onSave: (campaign: Partial<Campaign>, id?: number) => Promise<void>;
  onSaveVersand: (versand: Partial<Versand>, id?: number) => Promise<void>;
  onDeleteVersand: (id: number) => Promise<void>;
}

export default function CampaignDetailDialog({
  open, campaign, lotteries, werbewege, versandList, onClose, onSave, onSaveVersand, onDeleteVersand
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);

  // Campaign form state
  const [form, setForm] = useState({
    lottery_id: 0, name: '', kurz: '', description: '',
    start_date: '', end_date: '', status: 'draft', active: true
  });

  // Versand edit state
  const [editingVersand, setEditingVersand] = useState<Versand | null>(null);
  const [versandForm, setVersandForm] = useState({
    lotterie_id: 0, werbeweg_id: 0, versandbrief: '', inhalt_versandbrief: '',
    gutschrift_betrag: 0, gutschrift_prozent: 0,
    buchungstext: '', porto: 0, klasse_beginn: 0, prozent_boh: 100, prozent_bok: 0
  });

  // Filter versand for this campaign (nutzt campaign_id_resolved für aufgelöste Kampagnen)
  const campaignVersand = campaign
    ? versandList.filter(v => v.campaign_id_resolved === campaign.id)
    : [];

  useEffect(() => {
    if (campaign) {
      setForm({
        lottery_id: campaign.lottery_id,
        name: campaign.name,
        kurz: campaign.kurz || '',
        description: campaign.description || '',
        start_date: campaign.start_date.split('T')[0],
        end_date: campaign.end_date.split('T')[0],
        status: campaign.status,
        active: campaign.active !== false
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setForm({
        lottery_id: lotteries[0]?.id || 0,
        name: '', kurz: '', description: '',
        start_date: today,
        end_date: nextMonth.toISOString().split('T')[0],
        status: 'draft', active: true
      });
    }
    setTabValue(0);
    setEditingVersand(null);
  }, [campaign, lotteries, open]);

  const handleSaveCampaign = async () => {
    setSaving(true);
    try {
      await onSave(form, campaign?.id);
      if (!campaign) {
        onClose(); // Close on new campaign creation
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditVersand = (v: Versand) => {
    setEditingVersand(v);
    setVersandForm({
      lotterie_id: v.lotterie_id || 0,
      werbeweg_id: v.werbeweg_id || 0,
      versandbrief: v.versandbrief || '',
      inhalt_versandbrief: v.inhalt_versandbrief || '',
      gutschrift_betrag: v.gutschrift_betrag || 0,
      gutschrift_prozent: v.gutschrift_prozent || 0,
      buchungstext: v.buchungstext || '',
      porto: v.porto || 0,
      klasse_beginn: v.klasse_beginn || 0,
      prozent_boh: v.prozent_boh || 100,
      prozent_bok: v.prozent_bok || 0
    });
  };

  const handleNewVersand = () => {
    setEditingVersand({ id: 0, kampagnen_id: campaign?.id || 0 } as Versand);
    setVersandForm({
      lotterie_id: lotteries[0]?.id || 0,
      werbeweg_id: werbewege[0]?.id || 0,
      versandbrief: '', inhalt_versandbrief: '',
      gutschrift_betrag: 0, gutschrift_prozent: 0,
      buchungstext: '', porto: 0, klasse_beginn: 0,
      prozent_boh: 100, prozent_bok: 0
    });
  };

  const handleSaveVersand = async () => {
    if (!campaign) return;
    setSaving(true);
    try {
      await onSaveVersand({
        ...versandForm,
        kampagnen_id: campaign.id,
        verwendung_boh: versandForm.prozent_boh > 0,
        verwendung_bok: versandForm.prozent_bok > 0
      }, editingVersand?.id || undefined);
      setEditingVersand(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVersand = async (id: number) => {
    if (window.confirm('Versand-Eintrag wirklich löschen?')) {
      await onDeleteVersand(id);
    }
  };

  const handleBohChange = (value: number) => {
    const boh = Math.min(100, Math.max(0, value));
    setVersandForm({ ...versandForm, prozent_boh: boh, prozent_bok: 100 - boh });
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        {campaign ? `Kampagne: ${campaign.name}` : 'Neue Kampagne'}
        {campaign?.kurz && <Chip label={campaign.kurz} size="small" sx={{ ml: 1 }} />}
      </DialogTitle>

      <DialogContent>
        {campaign && (
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Stammdaten" />
            <Tab label={`Versand (${campaignVersand.length})`} />
          </Tabs>
        )}

        {/* Tab 0: Stammdaten */}
        <TabPanel value={tabValue} index={campaign ? 0 : -1}>
          <Stack spacing={2}>
            <FormControl fullWidth required>
              <InputLabel>Lotterie</InputLabel>
              <Select value={form.lottery_id} onChange={(e) => setForm({ ...form, lottery_id: Number(e.target.value) })} label="Lotterie" disabled={saving}>
                {lotteries.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={saving} required />
            <TextField fullWidth label="Kürzel" value={form.kurz} onChange={(e) => setForm({ ...form, kurz: e.target.value.slice(0, 2).toUpperCase() })} disabled={saving} helperText="Maximal 2 Zeichen" inputProps={{ maxLength: 2 }} />
            <TextField fullWidth label="Beschreibung" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={saving} />
            <TextField fullWidth type="date" label="Startdatum" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} InputLabelProps={{ shrink: true }} disabled={saving} required />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} label="Status" disabled={saving}>
                <MenuItem value="draft">Entwurf</MenuItem>
                <MenuItem value="active">Aktiv</MenuItem>
                <MenuItem value="paused">Pausiert</MenuItem>
                <MenuItem value="completed">Abgeschlossen</MenuItem>
              </Select>
            </FormControl>
            {campaign && (
              <Box sx={{ p: 2, bgcolor: form.active ? 'success.50' : 'warning.50', borderRadius: 1 }}>
                <FormControlLabel
                  control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} disabled={saving} />}
                  label={form.active ? 'Kampagne ist aktiv' : 'Kampagne ist deaktiviert'}
                />
                {!form.active && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Kürzel kann wiederverwendet werden. Bestehende Aktionen bleiben unberührt.
                  </Alert>
                )}
              </Box>
            )}
          </Stack>
        </TabPanel>

        {/* New Campaign Form (no tabs) */}
        {!campaign && (
          <Stack spacing={2} sx={{ pt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Lotterie</InputLabel>
              <Select value={form.lottery_id} onChange={(e) => setForm({ ...form, lottery_id: Number(e.target.value) })} label="Lotterie" disabled={saving}>
                {lotteries.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={saving} required />
            <TextField fullWidth label="Kürzel" value={form.kurz} onChange={(e) => setForm({ ...form, kurz: e.target.value.slice(0, 2).toUpperCase() })} disabled={saving} helperText="Maximal 2 Zeichen" inputProps={{ maxLength: 2 }} />
            <TextField fullWidth label="Beschreibung" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={saving} />
            <TextField fullWidth type="date" label="Startdatum" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} InputLabelProps={{ shrink: true }} disabled={saving} required />
          </Stack>
        )}

        {/* Tab 1: Versand */}
        {campaign && (
          <TabPanel value={tabValue} index={1}>
            {editingVersand ? (
              // Versand Edit Form
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {editingVersand.id ? 'Versand bearbeiten' : 'Neuer Versand'}
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Lotterie</InputLabel>
                      <Select value={versandForm.lotterie_id} onChange={(e) => setVersandForm({ ...versandForm, lotterie_id: Number(e.target.value) })} label="Lotterie">
                        {lotteries.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Werbeweg</InputLabel>
                      <Select value={versandForm.werbeweg_id} onChange={(e) => setVersandForm({ ...versandForm, werbeweg_id: Number(e.target.value) })} label="Werbeweg">
                        {werbewege.map(w => <MenuItem key={w.id} value={w.id}>{w.bezeichnung}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: 2 }}>
                    <TextField label="Versandbrief (Nr.)" value={versandForm.versandbrief} onChange={(e) => setVersandForm({ ...versandForm, versandbrief: e.target.value })} placeholder="z.B. 575" />
                    <TextField label="Inhalt Versandbrief" value={versandForm.inhalt_versandbrief} onChange={(e) => setVersandForm({ ...versandForm, inhalt_versandbrief: e.target.value })} placeholder="z.B. Extra Joker Plus" />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
                    <TextField type="number" label="Porto (€)" value={versandForm.porto} onChange={(e) => setVersandForm({ ...versandForm, porto: Number(e.target.value) })} inputProps={{ step: 0.01, min: 0 }} />
                    <TextField type="number" label="Gutschrift (€)" value={versandForm.gutschrift_betrag} onChange={(e) => setVersandForm({ ...versandForm, gutschrift_betrag: Number(e.target.value) })} inputProps={{ step: 0.01, min: 0 }} />
                    <TextField type="number" label="Gutschrift (%)" value={versandForm.gutschrift_prozent} onChange={(e) => setVersandForm({ ...versandForm, gutschrift_prozent: Number(e.target.value) })} inputProps={{ step: 0.01, min: 0, max: 100 }} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
                    <TextField label="Buchungstext" value={versandForm.buchungstext} onChange={(e) => setVersandForm({ ...versandForm, buchungstext: e.target.value })} />
                    <TextField type="number" label="Klasse Beginn" value={versandForm.klasse_beginn} onChange={(e) => setVersandForm({ ...versandForm, klasse_beginn: Number(e.target.value) })} />
                  </Box>
                  <Divider />
                  <Typography variant="subtitle1">BOH / BOK Verteilung</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField type="number" label="BOH %" value={versandForm.prozent_boh} onChange={(e) => handleBohChange(Number(e.target.value))} inputProps={{ min: 0, max: 100 }} helperText="Bestelloption Heft" />
                    <TextField type="number" label="BOK %" value={versandForm.prozent_bok} disabled helperText="Bestelloption Karte (automatisch)" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setEditingVersand(null)}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSaveVersand} disabled={saving}>Speichern</Button>
                  </Box>
                </Stack>
              </Box>
            ) : (
              // Versand List
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1">{campaignVersand.length} Versand-Einträge</Typography>
                  <Button startIcon={<Plus size={18} />} variant="outlined" size="small" onClick={handleNewVersand}>
                    Neuer Versand
                  </Button>
                </Box>
                {campaignVersand.length === 0 ? (
                  <Alert severity="info">Noch keine Versand-Einträge für diese Kampagne.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Werbeweg</TableCell>
                          <TableCell>Versandbrief</TableCell>
                          <TableCell>Inhalt</TableCell>
                          <TableCell>Porto</TableCell>
                          <TableCell>Kl.</TableCell>
                          <TableCell>Gutschrift</TableCell>
                          <TableCell>Buchungstext</TableCell>
                          <TableCell align="right">Akt.</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {campaignVersand.map((v) => (
                          <TableRow key={v.id} hover>
                            <TableCell sx={{ fontWeight: 'bold' }}>{v.werbeweg_name || '-'}</TableCell>
                            <TableCell>{v.versandbrief || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Tooltip title={v.inhalt_versandbrief || ''}><span>{v.inhalt_versandbrief || '-'}</span></Tooltip>
                            </TableCell>
                            <TableCell>{v.porto ? `${Number(v.porto).toFixed(2)}€` : '-'}</TableCell>
                            <TableCell>{v.klasse_beginn || '-'}</TableCell>
                            <TableCell>
                              {v.gutschrift_betrag ? `${Number(v.gutschrift_betrag).toFixed(2)}€` : '-'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.buchungstext || '-'}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <IconButton size="small" onClick={() => handleEditVersand(v)}><PencilSimple size={14} /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteVersand(v.id)}><Trash size={14} /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Schließen</Button>
        {(tabValue === 0 || !campaign) && (
          <Button onClick={handleSaveCampaign} variant="contained" sx={{ backgroundColor: '#003366' }} disabled={saving || !form.name}>
            Kampagne speichern
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
